import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiClient } from '../../lib/apiClient';
import { auditService } from '../../services/auditService';
import { Mail, Trash2, UserCheck, Clock, AlertTriangle, EditIcon } from '../icons/Icons';
import s from './shared.module.css';

interface TeamMember {
  id: string;
  user_id: string;
  display_name: string;
  email?: string;
  title?: string;
  role: string;
  user_type: string;
  seat_type: string;
  is_active: boolean;
  last_active_at: string | null;
  created_at: string;
  departments?: string[];
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  user_type: string;
  seat_type: string;
  status: string;
  invited_by: string;
  expires_at: string;
  created_at: string;
  department_ids?: string[];
}

interface Department {
  id: string;
  name: string;
  description: string;
}

interface OrgManager {
  id: string;
  user_id: string;
  display_name: string;
  title?: string;
  user_type: string;
}

interface SeatUsage {
  total_seats: number;
  active_seats: number;
  available_seats: number;
  seat_limit: number;
}

const USER_TYPE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  contributor: 'Contributor',
  viewer: 'Viewer',
  resource: 'Resource'
};

const USER_TYPE_DESCRIPTIONS: Record<string, string> = {
  owner: 'Full access to all features, billing, and organization management',
  admin: 'Full access to data and settings, can manage users and teams',
  manager: 'Can manage projects, resources, and team members within departments',
  contributor: 'Can create and edit own content, submit for approval',
  viewer: 'Read-only access to data and reports',
  resource: 'Timesheet-only access - can log hours but cannot view financials or other data'
};

const SEAT_TYPE_LABELS: Record<string, string> = {
  full: 'Full Seat',
  limited: 'Limited Seat',
  viewer: 'Viewer'
};

export default function TeamManagement() {
  const { organization, user, orgMember } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [orgManagers, setOrgManagers] = useState<OrgManager[]>([]);
  const [seatUsage, setSeatUsage] = useState<SeatUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editDepartmentIds, setEditDepartmentIds] = useState<string[]>([]);
  const [editUserType, setEditUserType] = useState('viewer');
  const [editSeatType, setEditSeatType] = useState('full');
  const [editTitle, setEditTitle] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteUserType, setInviteUserType] = useState('viewer');
  const [inviteSeatType, setInviteSeatType] = useState('full');
  const [inviteDepartmentIds, setInviteDepartmentIds] = useState<string[]>([]);
  const [invitePrimaryDepartmentId, setInvitePrimaryDepartmentId] = useState('');
  const [inviteManagerId, setInviteManagerId] = useState('');
  const [inviteIsResource, setInviteIsResource] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserType, setAddUserType] = useState('viewer');
  const [addSeatType, setAddSeatType] = useState('full');
  const [addDepartmentIds, setAddDepartmentIds] = useState<string[]>([]);
  const [addPrimaryDepartmentId, setAddPrimaryDepartmentId] = useState('');
  const [addManagerId, setAddManagerId] = useState('');
  const [addIsResource, setAddIsResource] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [searchedUser, setSearchedUser] = useState<{ id: string; email: string; display_name: string | null } | null>(null);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ email: string; emailSent: boolean; acceptUrl: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!organization) return;
    const data = await apiClient.get<TeamMember[]>(
      `/orgs/${organization.id}/members?withDepartments=true`
    );
    setMembers(data || []);
  }, [organization]);

  const loadInvitations = useCallback(async () => {
    if (!organization) return;
    const data = await apiClient.get<Invitation[]>(
      `/orgs/${organization.id}/invitations?status=pending`
    );
    setInvitations(data || []);
  }, [organization]);

  const loadSeatUsage = useCallback(async () => {
    if (!organization) return;
    try {
      const data = await apiClient.get<SeatUsage>(`/orgs/${organization.id}/seat-usage`);
      if (data) setSeatUsage(data);
    } catch { }
  }, [organization]);

  const loadDepartments = useCallback(async () => {
    if (!organization) return;
    try {
      const data = await apiClient.get<Department[]>(`/orgs/${organization.id}/departments`);
      setDepartments(data || []);
    } catch { }
  }, [organization]);

  const loadOrgManagers = useCallback(async () => {
    if (!organization) return;
    try {
      const data = await apiClient.get<OrgManager[]>(
        `/orgs/${organization.id}/members?types=owner,admin,manager`
      );
      setOrgManagers(data || []);
    } catch { }
  }, [organization]);

  const loadAll = useCallback(async () => {
    if (!organization) return;
    setLoading(true);
    setLoadError('');
    try {
      await Promise.all([loadMembers(), loadInvitations()]);
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load team data. Check that the backend is running.');
    } finally {
      setLoading(false);
    }
    loadSeatUsage();
    loadDepartments();
    loadOrgManagers();
  }, [organization, loadMembers, loadInvitations, loadSeatUsage, loadDepartments, loadOrgManagers]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization || !user) return;

    setSaving(true);
    setSaveError('');

    try {
      const roleMapping: Record<string, 'admin' | 'manager' | 'viewer'> = {
        'admin': 'admin',
        'manager': 'manager',
        'contributor': 'viewer',
        'resource': 'viewer',
        'viewer': 'viewer'
      };

      const invitationData = await apiClient.post<{ id: string; invitation_token: string; expires_at: string }>(
        `/orgs/${organization.id}/invitations`,
        {
          email: inviteEmail.toLowerCase(),
          role: roleMapping[inviteUserType] || 'viewer',
          user_type: inviteUserType,
          seat_type: inviteSeatType,
          department_ids: inviteDepartmentIds.length > 0 ? inviteDepartmentIds : [],
          primary_department_id: invitePrimaryDepartmentId || null,
          manager_id: inviteManagerId || null,
          invited_by: user.id,
          invited_by_name: orgMember?.display_name || user.email || 'Someone',
          is_resource: inviteIsResource,
        }
      );

      let emailSent = false;
      let acceptUrl = `${window.location.origin}/accept-invite/${invitationData.invitation_token}`;

      try {
        const emailResult = await apiClient.post<{ success: boolean; message: string }>(
          `/invitations/${invitationData.id}/send-email`,
          {
            email: inviteEmail,
            inviterName: orgMember?.display_name || user.email || 'Someone',
            organizationName: organization.name,
            role: roleMapping[inviteUserType] || 'viewer',
            userType: inviteUserType,
            invitationToken: invitationData.invitation_token,
            expiresAt: invitationData.expires_at,
          }
        );
        emailSent = !!(emailResult.success && emailResult.message === 'Invitation email sent successfully');
      } catch (emailError) {
        console.error('Failed to send invitation email:', emailError);
      }

      if (orgMember) {
        await auditService.log({
          orgId: organization.id,
          actorUserId: user.id,
          actorDisplayName: orgMember.display_name || user.email || '',
          action: 'member.invited',
          entityType: 'invitation',
          entityLabel: inviteEmail,
          newValues: { email: inviteEmail, role: roleMapping[inviteUserType] || 'viewer', user_type: inviteUserType }
        });
      }

      setShowInviteModal(false);
      setInviteEmail('');
      setInviteUserType('viewer');
      setInviteSeatType('full');
      setInviteDepartmentIds([]);
      setInvitePrimaryDepartmentId('');
      setInviteManagerId('');
      setInviteIsResource(false);
      setInviteResult({ email: inviteEmail.toLowerCase(), emailSent, acceptUrl });
      setLinkCopied(false);
      loadInvitations();
      loadSeatUsage();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to send invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!confirm('Revoke this invitation?')) return;
    await apiClient.patch(`/invitations/${invitationId}`, { status: 'revoked' });
    loadInvitations();
  };

  const roleFromUserType = (userType: string): 'admin' | 'manager' | 'viewer' => {
    if (userType === 'admin') return 'admin';
    if (userType === 'manager') return 'manager';
    return 'viewer';
  };

  const handleRoleChange = async (memberId: string, newUserType: string) => {
    const member = members.find(m => m.id === memberId);
    if (!member) return;

    const newRole = roleFromUserType(newUserType);

    await apiClient.patch(`/org-members/${memberId}`, { role: newRole, user_type: newUserType });

    if (organization && orgMember && user) {
      await auditService.log({
        orgId: organization.id,
        actorUserId: user.id,
        actorDisplayName: orgMember.display_name || user.email || '',
        action: 'member.role_changed',
        entityType: 'member',
        entityId: memberId,
        entityLabel: member.display_name,
        oldValues: { role: member.role, user_type: member.user_type },
        newValues: { role: newRole, user_type: newUserType }
      });
    }

    loadMembers();
  };

  const handleDeactivateMember = async (memberId: string) => {
    if (!confirm('Deactivate this member? They will lose access but data will be preserved.')) return;
    const member = members.find(m => m.id === memberId);

    await apiClient.patch(`/org-members/${memberId}`, { is_active: false });

    if (organization && orgMember && user && member) {
      await auditService.log({
        orgId: organization.id,
        actorUserId: user.id,
        actorDisplayName: orgMember.display_name || user.email || '',
        action: 'member.deactivated',
        entityType: 'member',
        entityId: memberId,
        entityLabel: member.display_name
      });
    }

    loadMembers();
    loadSeatUsage();
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Permanently remove this member? This action cannot be undone.')) return;
    const member = members.find(m => m.id === memberId);

    await apiClient.delete(`/org-members/${memberId}`);

    if (organization && orgMember && user && member) {
      await auditService.log({
        orgId: organization.id,
        actorUserId: user.id,
        actorDisplayName: orgMember.display_name || user.email || '',
        action: 'member.removed',
        entityType: 'member',
        entityId: memberId,
        entityLabel: member.display_name
      });
    }

    loadMembers();
    loadSeatUsage();
  };

  const handleEditMember = async (member: TeamMember) => {
    const currentDepts = await apiClient.get<Array<{ department_id: string }>>(
      `/users/${member.user_id}/department-memberships`
    );

    setEditingMember(member);
    setEditDepartmentIds(currentDepts?.map(d => d.department_id) || []);
    setEditUserType(member.user_type);
    setEditSeatType(member.seat_type);
    setEditTitle(member.title || '');
    setShowEditModal(true);
    setSaveError('');
  };

  const handleSearchUser = async () => {
    if (!addUserEmail.trim()) {
      setSaveError('Please enter an email address');
      return;
    }

    setSaving(true);
    setSaveError('');
    setSearchedUser(null);

    try {
      const foundUsers = await apiClient.get<Array<{ id: string; email: string; display_name: string | null }>>(
        `/users/search?email=${encodeURIComponent(addUserEmail.toLowerCase().trim())}`
      );

      if (!foundUsers || foundUsers.length === 0) {
        setSaveError('No user found with this email address. They need to create an account first.');
        return;
      }

      const foundUser = foundUsers[0];

      try {
        await apiClient.get(`/orgs/${organization!.id}/members/${foundUser.id}`);
        setSaveError('This user is already a member of your organization.');
        return;
      } catch (memberErr: any) {
        if (memberErr?.status !== 404) {
          setSaveError('This user is already a member of your organization.');
          return;
        }
      }

      setSearchedUser(foundUser);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to search for user');
    } finally {
      setSaving(false);
    }
  };

  const handleAddExistingUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchedUser || !organization || !user || !orgMember) return;

    setSaving(true);
    setSaveError('');

    try {
      const roleMapping: Record<string, 'admin' | 'manager' | 'viewer'> = {
        'admin': 'admin',
        'manager': 'manager',
        'contributor': 'viewer',
        'resource': 'viewer',
        'viewer': 'viewer'
      };

      await apiClient.post(`/orgs/${organization.id}/members`, {
        userId: searchedUser.id,
        role: roleMapping[addUserType] || 'viewer',
        user_type: addUserType,
        seat_type: addSeatType,
        display_name: searchedUser.display_name || searchedUser.email.split('@')[0],
        title: addTitle || undefined,
        primary_department_id: addPrimaryDepartmentId || null,
        department_ids: addDepartmentIds,
        manager_id: addManagerId || null,
        is_resource: addIsResource,
        is_active: true
      });

      await auditService.log({
        orgId: organization.id,
        actorUserId: user.id,
        actorDisplayName: orgMember.display_name || user.email || '',
        action: 'member.added',
        entityType: 'member',
        entityLabel: searchedUser.email,
        newValues: { email: searchedUser.email, user_type: addUserType, seat_type: addSeatType, title: addTitle }
      });

      setShowAddUserModal(false);
      setAddUserEmail('');
      setAddUserType('viewer');
      setAddSeatType('full');
      setAddDepartmentIds([]);
      setAddPrimaryDepartmentId('');
      setAddTitle('');
      setSearchedUser(null);
      setAddManagerId('');
      setAddIsResource(false);
      setSearchedUser(null);
      loadMembers();
      loadSeatUsage();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to add user to organization');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEditMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMember || !organization || !user || !orgMember) return;

    setSaving(true);
    setSaveError('');

    try {
      // Update role/seat type if changed
      const roleChanged = editUserType !== editingMember.user_type || editSeatType !== editingMember.seat_type;
      const titleChanged = editTitle !== (editingMember.title || '');
      if (roleChanged || titleChanged) {
        const newRole = roleFromUserType(editUserType);
        const patchBody: Record<string, string> = {
          role: newRole,
          user_type: editUserType,
          seat_type: editSeatType,
        };
        if (titleChanged) {
          patchBody.title = editTitle;
        }
        await apiClient.patch(`/org-members/${editingMember.id}`, patchBody);

        await auditService.log({
          orgId: organization.id,
          actorUserId: user.id,
          actorDisplayName: orgMember.display_name || user.email || '',
          action: 'member.role_changed',
          entityType: 'member',
          entityId: editingMember.id,
          entityLabel: editingMember.display_name,
          oldValues: { role: editingMember.role, user_type: editingMember.user_type, seat_type: editingMember.seat_type },
          newValues: { role: newRole, user_type: editUserType, seat_type: editSeatType }
        });
      }

      // Update department memberships
      const currentDepts = await apiClient.get<Array<{ department_id: string }>>(
        `/users/${editingMember.user_id}/department-memberships`
      );

      const currentDeptIds = currentDepts?.map(d => d.department_id) || [];
      const deptsToAdd = editDepartmentIds.filter(id => !currentDeptIds.includes(id));
      const deptsToRemove = currentDeptIds.filter(id => !editDepartmentIds.includes(id));

      if (deptsToRemove.length > 0) {
        await apiClient.post(`/users/${editingMember.user_id}/department-memberships/remove`, {
          department_ids: deptsToRemove,
        });
      }

      if (deptsToAdd.length > 0) {
        await apiClient.post(`/users/${editingMember.user_id}/department-memberships`, {
          org_id: organization.id,
          department_ids: deptsToAdd,
          added_by: user.id,
        });
      }

      if (deptsToAdd.length > 0 || deptsToRemove.length > 0) {
        await auditService.log({
          orgId: organization.id,
          actorUserId: user.id,
          actorDisplayName: orgMember.display_name || user.email || '',
          action: 'member.departments_updated',
          entityType: 'member',
          entityId: editingMember.id,
          entityLabel: editingMember.display_name,
          oldValues: { department_ids: currentDeptIds },
          newValues: { department_ids: editDepartmentIds }
        });
      }

      setShowEditModal(false);
      setEditingMember(null);
      setEditDepartmentIds([]);
      setEditUserType('viewer');
      setEditSeatType('full');
      setEditTitle('');
      loadMembers();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteResult) return;
    try {
      await navigator.clipboard.writeText(inviteResult.acceptUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch {
      // Fallback: select the text in the input
    }
  };

  const canManageUsers = orgMember?.user_type === 'owner' || orgMember?.user_type === 'admin';

  return (
    <div>
      {loading && (
        <div style={{ padding: '16px', color: '#605e5c', fontSize: '13px' }}>
          Loading team members...
        </div>
      )}
      {!loading && loadError && (
        <div style={{ padding: '12px 16px', marginBottom: '16px', background: '#fde7e9', border: '1px solid #f3b8a8', borderRadius: '4px', color: '#d13438', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span>{loadError}</span>
          <button
            onClick={loadAll}
            style={{ padding: '6px 14px', background: '#d13438', color: 'white', border: 'none', borderRadius: '3px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}
          >
            Retry
          </button>
        </div>
      )}
      {!loading && !loadError && (
        seatUsage ? (
        <div style={{
          padding: '16px',
          marginBottom: '24px',
          background: seatUsage.available_seats === 0 ? '#fde7e9' : seatUsage.available_seats < 3 ? '#fff4ce' : '#f3f2f1',
          border: `1px solid ${seatUsage.available_seats === 0 ? '#f3b8a8' : seatUsage.available_seats < 3 ? '#ffd97d' : '#e1dfdd'}`,
          borderRadius: '4px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <strong style={{ fontSize: '14px', color: '#323130' }}>Seat Usage</strong>
              <div style={{ fontSize: '13px', color: '#605e5c', marginTop: '4px' }}>
                {seatUsage.active_seats} of {seatUsage.seat_limit === -1 ? 'Unlimited' : seatUsage.seat_limit} seats used
                {seatUsage.seat_limit !== -1 && ` • ${seatUsage.available_seats} available`}
              </div>
            </div>
            {seatUsage.available_seats === 0 && (
              <AlertTriangle size={20} style={{ color: '#d13438' }} />
            )}
          </div>
        </div>
        ) : null
      )}

      <div className={s.card}>
        <div className={s.cardHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className={s.cardTitle}>Team Members ({members.filter(m => m.is_active).length} active)</h3>
          {canManageUsers && (
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              {/* <button className={s.secondaryBtn} onClick={() => setShowAddUserModal(true)}>
                Add Existing User
              </button> */}
              <button className={s.primaryBtn} onClick={() => setShowInviteModal(true)}>
                <Mail size={14} /> Invite Member
              </button>
            </div>
          )}
        </div>

        <table className={s.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>User Type</th>
              <th>Seat Type</th>
              <th>Departments</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan={7} className={s.emptyState}>{loading ? 'Loading...' : 'No members found'}</td></tr>
            )}
            {members.map(m => (
              <tr key={m.id} style={{ opacity: m.is_active ? 1 : 0.5 }}>
                <td style={{ fontWeight: 600 }}>
                  {m.display_name || m.email || '—'}
                  {m.title && (
                    <span style={{ fontSize: 11, color: '#605e5c', fontWeight: 400, display: 'block', marginTop: 2 }}>{m.title}</span>
                  )}
                  {m.user_id === user?.id && (
                    <span style={{ fontSize: 10, color: '#0078d4', marginLeft: 6, fontWeight: 400 }}>You</span>
                  )}
                </td>
                <td>
                  {m.user_id === user?.id || !canManageUsers ? (
                    <span style={{ fontSize: '13px',paddingLeft: '16px'}}>{USER_TYPE_LABELS[m.user_type] || m.user_type}</span>
                  ) : (
                    <select
                      className={s.filterSelect}
                      value={m.user_type}
                      onChange={e => handleRoleChange(m.id, e.target.value)}
                      style={{ fontSize: '13px' }}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="contributor">Contributor</option>
                      <option value="viewer">Viewer</option>
                      <option value="resource">Resource</option>
                    </select>
                  )}
                </td>
                <td style={{ fontSize: '12px', color: '#605e5c' }}>
                  {SEAT_TYPE_LABELS[m.seat_type] || m.seat_type}
                </td>
                <td>
                  {m.departments && m.departments.length > 0 ? (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {m.departments.map((dept, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 3,
                            background: '#e1dfdd',
                            color: '#323130',
                            fontWeight: 500
                          }}
                        >
                          {dept}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: '#a19f9d' }}>—</span>
                  )}
                </td>
                <td>
                  {m.is_active ? (
                    <span style={{ color: '#107c10', fontSize: '13px' }}>
                      <UserCheck size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Active
                    </span>
                  ) : (
                    <span style={{ color: '#a19f9d', fontSize: '13px' }}>Inactive</span>
                  )}
                </td>
                <td style={{ fontSize: '12px', color: '#605e5c' }}>
                  {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td>
                  {m.user_id !== user?.id && canManageUsers && (
                    <div className={s.actions}>
                      {m.is_active ? (
                        <>
                          <button
                            className={`${s.actionBtn} ${s.edit}`}
                            onClick={() => handleEditMember(m)}
                            title="Edit departments"
                          >
                            <EditIcon size={14} />
                          </button>
                          <button
                            className={`${s.actionBtn} ${s.edit}`}
                            onClick={() => handleDeactivateMember(m.id)}
                            title="Deactivate"
                          >
                            Deactivate
                          </button>
                          <button
                            className={`${s.actionBtn} ${s.del}`}
                            onClick={() => handleRemoveMember(m.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      ) : (
                        <button
                          className={`${s.actionBtn} ${s.del}`}
                          onClick={() => handleRemoveMember(m.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invitations.length > 0 && (
        <div className={s.card} style={{ marginTop: '24px' }}>
          <div className={s.cardHeader}>
            <h3 className={s.cardTitle}>Pending Invitations ({invitations.length})</h3>
          </div>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>User Type</th>
                <th>Invited</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => (
                <tr key={inv.id}>
                  <td style={{ fontWeight: 600 }}>{inv.email}</td>
                  <td>{USER_TYPE_LABELS[inv.user_type] || inv.user_type}</td>
                  <td style={{ fontSize: '12px', color: '#605e5c' }}>
                    {new Date(inv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </td>
                  <td style={{ fontSize: '12px', color: '#605e5c' }}>
                    <Clock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {new Date(inv.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </td>
                  <td>
                    {canManageUsers && (
                      <button
                        className={`${s.actionBtn} ${s.del}`}
                        onClick={() => handleRevokeInvitation(inv.id)}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={s.card} style={{ marginTop: '24px' }}>
        <div className={s.cardHeader}>
          <h3 className={s.cardTitle}>User Type Permissions</h3>
        </div>
        <div style={{ padding: '20px' }}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>User Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(USER_TYPE_DESCRIPTIONS).filter(([type]) => type !== 'owner').map(([type, description]) => (
                <tr key={type}>
                  <td style={{ fontWeight: 600 }}>{USER_TYPE_LABELS[type]}</td>
                  <td style={{ fontSize: '13px', color: '#605e5c' }}>{description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showInviteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>Invite Team Member</h3>

            <form onSubmit={handleInvite}>
              {saveError && (
                <div style={{
                  padding: '12px',
                  marginBottom: '16px',
                  backgroundColor: '#fde7e9',
                  border: '1px solid #f3b8a8',
                  borderRadius: '4px',
                  color: '#d13438',
                  fontSize: '13px'
                }}>
                  {saveError}
                </div>
              )}

              <div className={s.formGroup}>
                <label>Email Address *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                />
              </div>

              <div className={s.formGroup}>
                <label>User Type *</label>
                <select value={inviteUserType} onChange={e => setInviteUserType(e.target.value)} required>
                  <option value="viewer">Viewer - Read-only access</option>
                  <option value="resource">Resource - Timesheet-only access</option>
                  <option value="contributor">Contributor - Can create and edit own content</option>
                  <option value="manager">Manager - Can manage teams and approve</option>
                  <option value="admin">Admin - Full access except billing</option>
                </select>
                <div style={{ fontSize: '12px', color: '#605e5c', marginTop: '4px' }}>
                  {USER_TYPE_DESCRIPTIONS[inviteUserType]}
                </div>
              </div>

              <div className={s.formGroup}>
                <label>Seat Type *</label>
                <select value={inviteSeatType} onChange={e => setInviteSeatType(e.target.value)} required>
                  <option value="full">Full Seat - Complete access to assigned features</option>
                  <option value="limited">Limited Seat - Restricted feature set</option>
                  <option value="viewer">Viewer - Read-only, no seat count</option>
                </select>
              </div>

              <div className={s.formGroup}>
                <label>Primary Department (Optional)</label>
                <select value={invitePrimaryDepartmentId} onChange={e => setInvitePrimaryDepartmentId(e.target.value)}>
                  <option value="">— None —</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                  Sets the user's home department — used as the default for their records and access control
                </div>
              </div>

              <div className={s.formGroup}>
                <label>Reporting Manager (Optional)</label>
                <select value={inviteManagerId} onChange={e => setInviteManagerId(e.target.value)}>
                  <option value="">— None —</option>
                  {orgManagers.map(mgr => (
                    <option key={mgr.id} value={mgr.user_id}>{mgr.display_name}{mgr.title ? ` — ${mgr.title}` : ''} ({USER_TYPE_LABELS[mgr.user_type] || mgr.user_type})</option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                  Assigned as this user's manager for time-off approvals and reporting
                </div>
              </div>

              <div className={s.formGroup}>
                <label>Create as Resource</label>
                <div style={{ marginTop: 2, flexShrink: 0, width:'5%', float:'left' }}>
                 <input
                    type="checkbox"
                    checked={inviteIsResource}
                    onChange={(e) => setInviteIsResource(e.target.checked)}
                    style={{ marginTop: 2, flexShrink: 0}}
                  />
                  </div>
                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4, width: '90%' }}>
                  Automatically create a resource record for this user upon invitation acceptance. The resource will inherit their display name, email, and reporting manager.
                </div>
              </div>
                
              {/* <div className={s.formGroup}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={inviteIsResource}
                    onChange={(e) => setInviteIsResource(e.target.checked)}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span>
                    <span style={{ fontWeight: 600, color: '#323130' }}>Create as Resource</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#605e5c', marginTop: 2 }}>
                      Automatically create a resource record for this user upon invitation acceptance. The resource will inherit their display name, email, and reporting manager.
                    </span>
                  </span>
                </label>
              </div> */}

              <div className={s.formGroup}>
                <label>Department Access (Optional)</label>
                <div style={{
                  border: '1px solid #d2d0ce',
                  borderRadius: 4,
                  padding: 8,
                  maxHeight: 150,
                  overflowY: 'auto',
                  background: '#fff'
                }}>
                  {departments.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#8a8886', padding: 8, textAlign: 'center' }}>
                      No departments available. Create departments first.
                    </div>
                  ) : (
                    departments.map(dept => (
                      <label
                        key={dept.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderRadius: 3,
                          fontSize: 13
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f2f1'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={inviteDepartmentIds.includes(dept.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInviteDepartmentIds([...inviteDepartmentIds, dept.id]);
                            } else {
                              setInviteDepartmentIds(inviteDepartmentIds.filter(id => id !== dept.id));
                            }
                          }}
                          style={{ marginRight: 8, width:'5%'}}
                        />
                        <span style={{ fontWeight: 500, color: '#323130', width: '90%' }}>{dept.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                  Grant access to additional departments beyond the primary department
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className={s.secondaryBtn}
                  onClick={() => setShowInviteModal(false)}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={s.primaryBtn}
                  disabled={saving}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  {saving ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddUserModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>Add Existing User</h3>
            <p style={{ fontSize: '13px', color: '#605e5c', marginBottom: '20px' }}>
              Search for a user who already has an account and add them to your organization.
            </p>

            {saveError && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#fde7e9',
                border: '1px solid #f3b8a8',
                borderRadius: '4px',
                color: '#d13438',
                fontSize: '13px'
              }}>
                {saveError}
              </div>
            )}

            <div className={s.formGroup}>
              <label>Email Address *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="email"
                  value={addUserEmail}
                  onChange={e => setAddUserEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className={s.primaryBtn}
                  onClick={handleSearchUser}
                  disabled={saving || !addUserEmail.trim()}
                >
                  {saving ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            {searchedUser && (
              <>
                <div style={{
                  padding: '12px',
                  marginBottom: '16px',
                  backgroundColor: '#e8f4e8',
                  border: '1px solid #9dd99d',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}>
                  <strong style={{ color: '#107c10' }}>User found!</strong>
                  <div style={{ marginTop: '4px', color: '#323130' }}>
                    {searchedUser.display_name && <div><strong>{searchedUser.display_name}</strong></div>}
                    <div>{searchedUser.email}</div>
                  </div>
                </div>

                <form onSubmit={handleAddExistingUser}>
                  <div className={s.formGroup}>
                    <label>Job Title</label>
                    <input
                      type="text"
                      value={addTitle}
                      onChange={e => setAddTitle(e.target.value)}
                      placeholder="e.g. Director of Engineering"
                    />
                  </div>
                  <div className={s.formGroup}>
                    <label>User Type *</label>
                    <select value={addUserType} onChange={e => setAddUserType(e.target.value)} required>
                      <option value="viewer">Viewer - Read-only access</option>
                      <option value="resource">Resource - Timesheet-only access</option>
                      <option value="contributor">Contributor - Can create and edit own content</option>
                      <option value="manager">Manager - Can manage teams and approve</option>
                      <option value="admin">Admin - Full access except billing</option>
                    </select>
                    <div style={{ fontSize: '12px', color: '#605e5c', marginTop: '4px' }}>
                      {USER_TYPE_DESCRIPTIONS[addUserType]}
                    </div>
                  </div>

                  <div className={s.formGroup}>
                    <label>Seat Type *</label>
                    <select value={addSeatType} onChange={e => setAddSeatType(e.target.value)} required>
                      <option value="full">Full Seat - Complete access to assigned features</option>
                      <option value="limited">Limited Seat - Restricted feature set</option>
                      <option value="viewer">Viewer - Read-only, no seat count</option>
                    </select>
                  </div>

                  <div className={s.formGroup}>
                    <label>Primary Department (Optional)</label>
                    <select value={addPrimaryDepartmentId} onChange={e => setAddPrimaryDepartmentId(e.target.value)}>
                      <option value="">— None —</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                    <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                      Sets the user's home department for their records and access control
                    </div>
                  </div>

                  <div className={s.formGroup}>
                    <label>Reporting Manager (Optional)</label>
                    <select value={addManagerId} onChange={e => setAddManagerId(e.target.value)}>
                      <option value="">— None —</option>
                      {orgManagers.map(mgr => (
                        <option key={mgr.id} value={mgr.user_id}>{mgr.display_name}{mgr.title ? ` — ${mgr.title}` : ''} ({USER_TYPE_LABELS[mgr.user_type] || mgr.user_type})</option>
                      ))}
                    </select>
                    <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                      Assigned as this user's manager for time-off approvals and reporting
                    </div>
                  </div>

                  <div className={s.formGroup}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={addIsResource}
                        onChange={(e) => setAddIsResource(e.target.checked)}
                        style={{ marginTop: 2, flexShrink: 0 }}
                      />
                      <span>
                        <span style={{ fontWeight: 600, color: '#323130' }}>Create as Resource</span>
                        <span style={{ display: 'block', fontSize: 12, color: '#605e5c', marginTop: 2 }}>
                          Automatically create a resource record for this user. The resource will inherit their display name, email, and reporting manager.
                        </span>
                      </span>
                    </label>
                  </div>

                  <div className={s.formGroup}>
                    <label>Department Access (Optional)</label>
                    <div style={{
                      border: '1px solid #d2d0ce',
                      borderRadius: 4,
                      padding: 8,
                      maxHeight: 150,
                      overflowY: 'auto',
                      background: '#fff'
                    }}>
                      {departments.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#8a8886', padding: 8, textAlign: 'center' }}>
                          No departments available. Create departments first.
                        </div>
                      ) : (
                        departments.map(dept => (
                          <label
                            key={dept.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '6px 8px',
                              cursor: 'pointer',
                              borderRadius: 3,
                              fontSize: 13
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f2f1'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <input
                              type="checkbox"
                              checked={addDepartmentIds.includes(dept.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAddDepartmentIds([...addDepartmentIds, dept.id]);
                                } else {
                                  setAddDepartmentIds(addDepartmentIds.filter(id => id !== dept.id));
                                }
                              }}
                              style={{ marginRight: 8 }}
                            />
                            <span style={{ fontWeight: 500, color: '#323130' }}>{dept.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                    <button
                      type="button"
                      className={s.secondaryBtn}
                      onClick={() => {
                        setShowAddUserModal(false);
                        setAddUserEmail('');
                        setSearchedUser(null);
                        setSaveError('');
                        setAddUserType('viewer');
                        setAddSeatType('full');
                        setAddDepartmentIds([]);
                        setAddPrimaryDepartmentId('');
                        setAddTitle('');
                        setAddManagerId('');
                        setAddIsResource(false);
                      }}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={s.primaryBtn}
                      disabled={saving}
                      style={{ flex: 1 }}
                    >
                      {saving ? 'Adding...' : 'Add to Organization'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {!searchedUser && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className={s.secondaryBtn}
                  onClick={() => {
                    setShowAddUserModal(false);
                    setAddUserEmail('');
                    setSearchedUser(null);
                    setSaveError('');
                    setAddPrimaryDepartmentId('');
                    setAddManagerId('');
                  }}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showEditModal && editingMember && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '4px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>
              Edit Member — {editingMember.display_name}
            </h3>

            <form onSubmit={handleSaveEditMember}>
              {saveError && (
                <div style={{
                  padding: '12px',
                  marginBottom: '16px',
                  backgroundColor: '#fde7e9',
                  border: '1px solid #f3b8a8',
                  borderRadius: '4px',
                  color: '#d13438',
                  fontSize: '13px'
                }}>
                  {saveError}
                </div>
              )}

              <div className={s.formGroup}>
                <label>Job Title</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  placeholder="e.g. Director of Engineering"
                />
              </div>

              <div className={s.formRow}>
                <div className={s.formGroup}>
                  <label>User Type</label>
                  <select value={editUserType} onChange={e => setEditUserType(e.target.value)}>
                    <option value="viewer">Viewer</option>
                    <option value="resource">Resource</option>
                    <option value="contributor">Contributor</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                  <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                    {USER_TYPE_DESCRIPTIONS[editUserType]}
                  </div>
                </div>
                <div className={s.formGroup}>
                  <label>Seat Type</label>
                  <select value={editSeatType} onChange={e => setEditSeatType(e.target.value)}>
                    <option value="full">Full Seat</option>
                    <option value="limited">Limited Seat</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>

              <div className={s.formGroup}>
                <label>Departments</label>
                <div style={{
                  border: '1px solid #d2d0ce',
                  borderRadius: 4,
                  padding: 8,
                  maxHeight: 200,
                  overflowY: 'auto',
                  background: '#fff'
                }}>
                  {departments.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#8a8886', padding: 8, textAlign: 'center' }}>
                      No departments available. Create departments first.
                    </div>
                  ) : (
                    departments.map(dept => (
                      <label
                        key={dept.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderRadius: 3,
                          fontSize: 13
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f2f1'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={editDepartmentIds.includes(dept.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditDepartmentIds([...editDepartmentIds, dept.id]);
                            } else {
                              setEditDepartmentIds(editDepartmentIds.filter(id => id !== dept.id));
                            }
                          }}
                          style={{ marginRight: 8, width:'5%', float: 'left' }}
                        />
                        <div>
                          <div style={{ fontWeight: 500, color: '#323130' }}>{dept.name}</div>
                          {dept.description && (
                            <div style={{ fontSize: 11, color: '#8a8886', marginTop: 2 }}>
                              {dept.description}
                            </div>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                  Select departments this user should have access to
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className={s.secondaryBtn}
                  onClick={() => setShowEditModal(false)}
                  style={{ flex: 1,justifyContent: 'center' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={s.primaryBtn}
                  disabled={saving}
                  style={{ flex: 1,justifyContent: 'center' }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {inviteResult && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '4px',
            padding: '28px',
            width: '90%',
            maxWidth: '520px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
          }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: inviteResult.emailSent ? '#e8f4e8' : '#fff4ce',
                fontSize: 18
              }}>
                {inviteResult.emailSent ? '✓' : '!'}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: '#323130' }}>
                  {inviteResult.emailSent ? 'Invitation sent' : 'Invitation created'}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#605e5c' }}>
                  {inviteResult.emailSent
                    ? `An email has been sent to ${inviteResult.email}.`
                    : `Email could not be sent to ${inviteResult.email}. Share the link below directly.`}
                </p>
              </div>
            </div>

            {/* Link section */}
            <div style={{
              background: inviteResult.emailSent ? '#f3f2f1' : '#fff4ce',
              border: `1px solid ${inviteResult.emailSent ? '#e1dfdd' : '#ffd97d'}`,
              borderRadius: 4,
              padding: 14,
              marginBottom: 20
            }}>
              {!inviteResult.emailSent && (
                <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: '#605e5c' }}>
                  Share this invitation link with {inviteResult.email}:
                </p>
              )}
              {inviteResult.emailSent && (
                <p style={{ margin: '0 0 10px', fontSize: 13, color: '#605e5c' }}>
                  You can also share this link directly:
                </p>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  readOnly
                  value={inviteResult.acceptUrl}
                  onFocus={e => e.target.select()}
                  style={{
                    flex: 1,
                    fontSize: 12,
                    padding: '7px 10px',
                    border: '1px solid #d2d0ce',
                    borderRadius: 3,
                    background: 'white',
                    color: '#323130',
                    fontFamily: 'monospace',
                    minWidth: 0
                  }}
                />
                <button
                  onClick={handleCopyLink}
                  style={{
                    padding: '7px 14px',
                    border: '1px solid',
                    borderRadius: 3,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'all 0.15s',
                    background: linkCopied ? '#107c10' : '#0078d4',
                    borderColor: linkCopied ? '#107c10' : '#0078d4',
                    color: 'white',
                  }}
                >
                  {linkCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className={s.primaryBtn}
                onClick={() => setInviteResult(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
